import { AccessCardField } from "@/modules/dashboard/settings/components/access/AccessCardField";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import type { DirectoryEntry } from "@/shared/hooks/useOrganizationDirectory";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { ApiKeyRow } from "../hooks/useApiKeysCatalog";
import { useLastUsedLabel } from "../utils/lastUsed";
import { ApiKeyAccessCell } from "./ApiKeyAccessCell";
import { ApiKeyIdentityCell } from "./ApiKeyIdentityCell";
import { ApiKeyOrganizationCell } from "./ApiKeyOrganizationCell";
import { ApiKeyRowActions } from "./ApiKeyRowActions";
import { ApiKeyStatusPill } from "./ApiKeyStatusPill";

/**
 * Everything both renderers of the key catalog need. `ApiKeysTableProps` extends this instead of repeating
 * it, so the page can build one object and hand it to whichever renderer the breakpoint picks — and the two
 * cannot drift apart.
 */
export interface ApiKeysCardsProps {
  rows: readonly ApiKeyRow[];
  loading: boolean;
  catalogUnavailable: boolean;
  canUpdate: boolean;
  canManageRoles: boolean;
  /** Hidden for a non-INTERNAL viewer: their keys are all their own, so the column would be a constant. */
  showOrganization: boolean;
  lookupOrganization: (id: string | null | undefined) => DirectoryEntry | undefined;
  isDirectoryPending: boolean;
  isOrganizationPending: (id: string | null | undefined) => boolean;
  onManageRoles: (row: ApiKeyRow) => void;
  onEditIps: (row: ApiKeyRow) => void;
  onDisable: (row: ApiKeyRow) => void;
  onEnable: (row: ApiKeyRow) => void;
}

/** The same rows as `ApiKeysTable`, below `lg`. Same data, same actions, one card per key. */
export function ApiKeysCards({
  rows,
  loading,
  catalogUnavailable,
  canUpdate,
  canManageRoles,
  showOrganization,
  lookupOrganization,
  isDirectoryPending,
  isOrganizationPending,
  onManageRoles,
  onEditIps,
  onDisable,
  onEnable,
}: ApiKeysCardsProps) {
  const { t } = useTranslations("settings");
  const lastUsedLabel = useLastUsedLabel();

  if (loading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <ColocaSkeleton className="h-40 w-full" />
        <ColocaSkeleton className="h-40 w-full" />
        <ColocaSkeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {rows.map((row) => {
        const canOpenRoles = !catalogUnavailable && !row.rolesFailed;
        return (
          <ColocaSurface
            key={row.key.id}
            padding="md"
            className={cn("flex flex-col gap-3", row.status !== "active" && "opacity-60")}
          >
            <div className="flex items-start justify-between gap-2">
              <ApiKeyIdentityCell apiKey={row.key} lastUsedLabel={lastUsedLabel(row.key.last_used_at)} />
              <ApiKeyStatusPill status={row.status} />
            </div>

            {showOrganization && (
              <AccessCardField label={t("access_apikeys_col_organization")}>
                <ApiKeyOrganizationCell
                  organizationId={row.key.organization_id}
                  entry={lookupOrganization(row.key.organization_id)}
                  isDirectoryPending={isDirectoryPending}
                  isIdPending={isOrganizationPending(row.key.organization_id)}
                />
              </AccessCardField>
            )}

            <AccessCardField label={t("access_col_roles")}>
              <ApiKeyAccessCell
                row={row}
                catalogUnavailable={catalogUnavailable}
                onOpen={canOpenRoles ? () => onManageRoles(row) : undefined}
              />
            </AccessCardField>

            <div className="border-t border-dashed border-border pt-2.5">
              <ApiKeyRowActions
                row={row}
                canOpenRoles={canOpenRoles}
                canUpdate={canUpdate}
                canManageRoles={canManageRoles}
                onManageRoles={onManageRoles}
                onEditIps={onEditIps}
                onDisable={onDisable}
                onEnable={onEnable}
                spread
              />
            </div>
          </ColocaSurface>
        );
      })}
    </div>
  );
}
